import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { pick } from 'lodash';
import { Modal, Button, FormGroup, ControlLabel, FormControl, HelpBlock } from 'react-bootstrap';
import Joi from 'joi';
import Fa from 'react-fontawesome';
import Toggle from 'react-bootstrap-toggle';
import { Field, reduxForm, SubmissionError, formValueSelector } from 'redux-form';
// Import Actions
import { fetchSAMLSettings, updateSAMLSettings } from '../../AdminActions';
import ProgressBar from '../../../../components/progress-bar';
import styles from './styles.css';

class SAMLForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      valid: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.renderField = this.renderField.bind(this);
    this.onToggleEnable = this.onToggleEnable.bind(this);
    this.close = this.close.bind(this);
    this.loadData();
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onToggleEnable() {
    const { samlEnable } = this.props;
    this.props.change('saml_enable', !samlEnable);
  }

  loadData() {
    this.props.dispatch(fetchSAMLSettings(this.props.organization._id))
      .then(
        (data) => {
          this.initState(data);
        },
        (e) => {
          const data = e.data || e;
          this.setState({ error: data });
        },
      );
  }

  initState(data) {
    this.props.initialize(data);
    this.setState({ loaded: true });
  }

  close() {
    this.props.onModalClose();
  }

  handleSubmit(data) {
    const { organization } = this.props;
    const params = pick(data, ['saml_enable', 'saml_sso_url', 'saml_issuer', 'saml_cert']);
    return this.props.dispatch(updateSAMLSettings(organization._id, params))
      .then(
        (results) => {
          this.props.onUpdate(results);
          this.initState(results);
          if (this.props.modal) {
            this.close();
          }
          const el = (<div>SAML Settings Updated!</div>);
          this.context.addNotification(el, { type: 'success', autoClose: false });
        },
        (errors) => {
          const e = errors.data || errors;
          if (e.message) {
            e._error = e.message;
          } else if (e.permissions) {
            e._error = 'Unable to update SAML Setttings';
          }
          throw new SubmissionError(e);
        },
      );
  }

  renderField({
    input, label, type, meta: { touched, error }, componentClass, placeholder,
  }) {
    const { submitting } = this.props;
    const validationState = touched && error ? 'error' : null;
    const attrs = { ...input };
    if (componentClass === 'textarea') {
      attrs.rows = 10;
      attrs.className = styles.textarea;
    }
    return (
      <FormGroup validationState={validationState}>
        {label !== null && label !== undefined && label.length > 0 && <ControlLabel>{label}:</ControlLabel>}
        <FormControl {...attrs} componentClass={componentClass} placeholder={(placeholder || label)} type={type} disabled={submitting} />
        <FormControl.Feedback />
        {touched && error && <HelpBlock>{error.join('. ')}</HelpBlock>}
      </FormGroup>
    );
  }

  renderBody() {
    const {
      handleSubmit, submitting, modal, error, samlEnable,
    } = this.props;
    const formValid = this.props.valid;

    return (
      <form onSubmit={handleSubmit(this.handleSubmit)}>
        <Modal.Body>
          {error !== null &&
            <div className="alert alert-danger">
              {error}
            </div>
          }
          <FormGroup>
            <ControlLabel>Enable SAML:</ControlLabel>
            <div>
              <Toggle
                onClick={this.onToggleEnable}
                on={<span>Enabled</span>}
                off={<span>Disabled</span>}
                size="xs"
                onstyle="success"
                offstyle="danger"
                active={samlEnable}
              />
            </div>
          </FormGroup>
          <Field name="saml_sso_url" type="text" component={this.renderField} label="Identity Provider Single Sign-On URL" />
          <Field name="saml_issuer" type="text" component={this.renderField} label="Identity Provider Issuer" />
          <Field name="saml_cert" type="textarea" componentClass="textarea" component={this.renderField} label="Certificate" />
        </Modal.Body>
        <Modal.Footer className="clearfix">
          {modal && <Button className="pull-left" bsStyle="link" disabled={submitting} onClick={this.close}>Cancel</Button>}
          <Button className="pull-right" bsStyle="primary" disabled={(!formValid || submitting)} type="submit">
            {!submitting && <span><Fa name="pencil" /> Update</span>}
            {submitting && <span><Fa name="spinner" spin /> Updating</span>}
          </Button>
        </Modal.Footer>
      </form>
    )
  }

  render() {
    const { organization, modal } = this.props;
    const { loaded  } = this.state;

    let body = (
      <Modal.Body>
        <ProgressBar />
        <p />
      </Modal.Body>
    );
    if (loaded) {
      body = this.renderBody();
    }
    const title = `Manage SAML Settings for ${organization.display_name}`;

    if (!modal) {
      return (
        <div className="panel panel-default">
          <div className="panel-heading">
            <div className="panel-title">{title}</div>
          </div>
          <div className="panel-body">
            {body}
          </div>
        </div>
      )
    }

    return (
      <Modal show={this.props.show} onHide={this.close}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        {body}
      </Modal>
    );
  }
}

// Retrieve data from store as props
const selector = formValueSelector('samlForm'); // <-- same as form name
function mapStateToProps(state) {
  return {
    samlEnable: selector(state, 'saml_enable'),
  };
}

SAMLForm.propTypes = {
  initialize: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  change: PropTypes.func,
  reset: PropTypes.func,
  onUpdate: PropTypes.func,
  organization: PropTypes.shape({
    _id: PropTypes.string,
    display_name: PropTypes.string,
  }).isRequired,
  show: PropTypes.bool,
  modal: PropTypes.bool,
  onModalClose: PropTypes.func,
  error: PropTypes.string,
  valid: PropTypes.bool,
  samlEnable: PropTypes.bool,
};
SAMLForm.defaultProps = {
  onUpdate: () => {},
  samlEnable: false,
  show: false,
  modal: true,
  onModalClose: () => {},
  error: null,
  valid: false,
};
SAMLForm.contextTypes = {
  addNotification: PropTypes.func.isRequired,
};

const validate = (values) => {
  // Legacy constraints for reference:
  // const constraints = {
  //   saml_enable: {
  //     type: 'boolean',
  //     presence: { allowEmpty: false },
  //   },
  //   saml_sso_url: {
  //     type: 'string',
  //     presence: { allowEmpty: false },
  //     url: true,
  //   },
  //   saml_issuer: {
  //     type: 'string',
  //     presence: { allowEmpty: false },
  //     url: true,
  //   },
  //   saml_cert: {
  //     type: 'string',
  //     presence: { allowEmpty: false },
  //   },
  // };

  const schema = Joi.object({
    saml_enable: Joi.boolean().required().messages({
      'boolean.base': 'SAML Enable must be true or false',
      'any.required': 'SAML Enable is required',
    }),
    saml_sso_url: Joi.string().uri().required().messages({
      'string.base': 'SSO URL must be a string',
      'string.empty': 'SSO URL is required',
      'any.required': 'SSO URL is required',
      'string.uri': 'SSO URL must be a valid URL',
    }),
    saml_issuer: Joi.string().uri().required().messages({
      'string.base': 'Issuer must be a string',
      'string.empty': 'Issuer is required',
      'any.required': 'Issuer is required',
      'string.uri': 'Issuer must be a valid URL',
    }),
    saml_cert: Joi.string().required().messages({
      'string.base': 'Certificate must be a string',
      'string.empty': 'Certificate is required',
      'any.required': 'Certificate is required',
    }),
  }).unknown(true);

  const { error } = schema.validate(values, { abortEarly: false });
  if (!error) return {};
  
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
};

export default connect(mapStateToProps)(reduxForm({
  form: 'samlForm',
  validate,
})(SAMLForm));
